import { useNavigate } from 'react-router';

import GrvArrowRight from '../../../../assets/grv-arrow-right.svg?react';
import { useEntitlements } from '../../../../contexts/useEntitlements';
import { useBowser } from '../../../../hooks/useBowser';
import type { Artifact } from '../../../../types';
import { formatFileSize } from '../../../../utils/downloadUtils';
import {
  getLatestVersionFromArray,
  getVersionProperty,
} from '../../../../utils/entitlementUtils';

import './DownloadHomeTable.scss';

export const DownloadHomeTable = () => {
  const navigate = useNavigate();
  const { isMobile } = useBowser();
  const { entitlements } = useEntitlements();

  const handleKeyDown = (
    event: React.KeyboardEvent,
    artifactKey: string,
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/details/${artifactKey}`);
    }
  };

  if (
    !entitlements ||
    !entitlements.artifacts ||
    entitlements.artifacts.length === 0
  ) {
    return (
      <div className="download-home-table__status">
        <div className="download-home-table__status__error">
          No artifacts available
        </div>
      </div>
    );
  }

  const tableData = entitlements.artifacts.map(
    ({ artifactKey, artifactName, versions }: Artifact) => {
      const latestVersion = getLatestVersionFromArray(versions);

      return (
        <tr
          className="download-home-table__body-row"
          key={artifactKey}
          onClick={() => navigate(`/details/${artifactKey}`)}
          onKeyDown={(event) => handleKeyDown(event, artifactKey)}
          tabIndex={0}
          role="button"
          aria-label={`View details for ${artifactName}`}
          data-testid="download-home-table__body-row"
        >
          <th
            scope="row"
            className="download-home-table__row-text download-home-table__row-text-artifact"
          >
            {artifactName}
          </th>
          <td className="download-home-table__row-text">
            {getVersionProperty(latestVersion, 'version')}
          </td>
          <td className="download-home-table__row-text">
            {getVersionProperty(latestVersion, 'releaseDate')}
          </td>
          <td className="download-home-table__row-text">
            {latestVersion?.fileSize
              ? formatFileSize(latestVersion.fileSize)
              : 'N/A'}
          </td>
          <td className="download-home-table__row-text">
            <div className="download-home-table__row-arrow">
              <GrvArrowRight className="download-home-table__row-arrow-icon" />
            </div>
          </td>
        </tr>
      );
    },
  );

  const mobileTableData = entitlements.artifacts.map(
    ({ artifactKey, artifactName, versions }: Artifact) => {
      const latestVersion = getLatestVersionFromArray(versions);

      return (
        <div
          className="download-home-table__mobile-row"
          key={artifactKey}
          onClick={() => navigate(`/details/${artifactKey}`)}
          onKeyDown={(event) => handleKeyDown(event, artifactKey)}
          tabIndex={0}
          role="button"
          aria-label={`View details for ${artifactName}`}
        >
          <div className="download-home-table__mobile-row-info">
            <h3 className="download-home-table__mobile-row-title">
              {artifactName}
            </h3>
            <p className="download-home-table__mobile-row-version">
              Version: {getVersionProperty(latestVersion, 'version')}
            </p>
            <p className="download-home-table__mobile-row-release-date">
              Release date: {getVersionProperty(latestVersion, 'releaseDate')}
            </p>
          </div>
          <GrvArrowRight className="download-home-table__mobile-row-arrow-icon" />
        </div>
      );
    },
  );

  return (
    <div className="download-home-table__container">
      <div className="download-home-table__header">
        <h2 className="download-home-table__header-text">AirKey artifacts</h2>
      </div>
      {isMobile ? (
        <div className="download-home-table__mobile-table-container">
          {mobileTableData}
        </div>
      ) : (
        <div className="download-home-table__table-container">
          <table className="download-home-table">
            <caption className="download-home-table__caption">
              AirKey artifacts
            </caption>
            <thead className="download-home-table__head-container">
              <tr className="download-home-table__head-row">
                <th
                  scope="col"
                  className="download-home-table__head-text download-home-table__head-text-artifact"
                >
                  Artifact
                </th>
                <th scope="col" className="download-home-table__head-text">
                  Version
                </th>
                <th scope="col" className="download-home-table__head-text">
                  Release date
                </th>
                <th scope="col" className="download-home-table__head-text">
                  File size
                </th>
              </tr>
            </thead>
            <tbody>{tableData}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
